import { useOrgLockState } from '@/hooks/useOrgLockState';
import { useOpenAdminPanel } from '@/hooks/useOpenAdminPanel';

interface Props {
  /** The selected group's organization — drives the role-specific copy. */
  organizationId: string;
}

/**
 * Centered placeholder card that replaces a REST-backed content pane
 * (roster/events/polls/sign-ups) when the selected group's org is paused
 * (PR 6). The pane's API calls are skipped upstream — locked orgs 403 — so
 * this renders instead of a doomed fetch. Never says "suspended".
 */
export default function LockedPaneCard({ organizationId }: Props) {
  const { lockInfoFor } = useOrgLockState();
  const { openAdminPanel, opening } = useOpenAdminPanel();
  const info = lockInfoFor(organizationId);
  if (!info) return null;

  const churchAdmin = info.isAdmin && !info.isPersonal;
  const personalAdmin = info.isAdmin && info.isPersonal;

  let body: string;
  if (churchAdmin) {
    body =
      'Your church’s plan needs attention. Update billing to restore access for everyone — nothing has been deleted.';
  } else if (personalAdmin) {
    body =
      'Your plan is billed through the App Store — open Flock on your iPhone or iPad to change it, or remove extra members. Nothing has been deleted.';
  } else {
    body =
      'This group’s plan needs attention. Your admin can restore access — nothing has been deleted, and everything will be right where you left it.';
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-flock-gray-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#FEF3C7] text-3xl">
          🕊️
        </div>
        <h2 className="text-lg font-semibold text-flock-navy-900">
          {info.orgName} is paused
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-flock-navy-600">{body}</p>

        {churchAdmin && (
          <button
            onClick={() => openAdminPanel()}
            disabled={opening}
            className="min-h-touch mt-6 inline-flex w-full items-center justify-center rounded-lg bg-flock-navy-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-flock-navy-700 disabled:opacity-60"
          >
            {opening ? 'Opening…' : 'Update Plan in Admin Panel'}
          </button>
        )}

        <p className="mt-6 text-xs text-flock-navy-400">
          Need help? support@flock.church
        </p>
      </div>
    </div>
  );
}
